import { useQuery } from "@apollo/client";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StorefrontIcon from "@mui/icons-material/Storefront";
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import gql from "graphql-tag";
import UserContext from "../../context/User";
import { restaurantBrief } from "../../apollo/server";
import { resizedImage } from "../../utils/resizedImage";

const RESTAURANT_BRIEF = gql`
  ${restaurantBrief}
`;

const LINE = "#E8EAED";
const MUTED = "#6B7280";
const INK = "#111827";
const ACCENT = "#FF3008";

/**
 * The customer's carts.
 *
 * Two callers, two shapes:
 *  - Checkout keeps the default title and lists only the carts that aren't
 *    being checked out. "Other carts" means something there, because the cart
 *    it's other THAN is on screen above it.
 *  - The /carts picker passes `includeActive` and no title. On a page called
 *    "Your carts", splitting the same objects into "current" and "other" is a
 *    distinction without a difference — they're all just carts.
 *
 * Renders nothing when there is nothing to list.
 */
function CartsList({ title = "Other carts", includeActive = false }) {
  const navigate = useNavigate();
  const {
    otherCarts,
    switchCart,
    discardCart,
    cart,
    cartCount,
    restaurant,
    clearCart,
  } = useContext(UserContext);

  // The active cart's restaurant, named from the meta captured when its items
  // were added.
  let activeMeta = null;
  try {
    activeMeta = JSON.parse(localStorage.getItem("cartRestaurantMeta") || "null");
  } catch (e) {
    activeMeta = null;
  }

  const rows = [];
  if (includeActive && restaurant && cart?.length) {
    rows.push({
      restaurantId: String(restaurant),
      meta: activeMeta || { _id: restaurant },
      count: cartCount,
      isActive: true,
    });
  }
  (otherCarts || []).forEach((c) => rows.push({ ...c, isActive: false }));

  if (!rows.length) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: "18px",
        border: `1px solid ${LINE}`,
        backgroundColor: "#fff",
        boxShadow: "0 8px 28px rgba(17, 24, 39, 0.06)",
        px: { xs: 2, sm: 2.5 },
        py: 2.5,
      }}
    >
      {title ? (
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: MUTED,
            mb: 1.5,
          }}
        >
          {title}
        </Typography>
      ) : null}

      {rows.map((row, index) => (
        <CartRow
          key={row.restaurantId}
          cart={row}
          first={index === 0 && !title}
          // Checking out a cart that isn't active makes it active first, so the
          // button means the same thing on every row.
          onCheckout={() => {
            if (!row.isActive) switchCart(row.restaurantId);
            navigate("/checkout");
          }}
          onAddMore={(slug) => {
            let target = slug;
            if (!row.isActive) {
              const meta = switchCart(row.restaurantId);
              target = meta?.slug || slug;
            }
            if (target) navigate(`/restaurant/${target}`);
          }}
          onDiscard={() => {
            if (!window.confirm("Remove this cart?")) return;
            if (row.isActive) clearCart();
            else discardCart(row.restaurantId);
          }}
        />
      ))}
    </Paper>
  );
}

/**
 * One cart.
 *
 * `meta` is captured when items are added, but a cart parked before that
 * existed — or through a path that never had it — arrives with nothing to show,
 * and it never says whether the restaurant is still switched on. So every row
 * looks the restaurant up: a disabled one keeps its row (so the cart can be
 * removed) but can't be checked out or added to.
 */
function CartRow({ cart: c, first, onCheckout, onAddMore, onDiscard }) {
  const { data } = useQuery(RESTAURANT_BRIEF, {
    variables: { id: String(c.restaurantId) },
    fetchPolicy: "cache-and-network",
  });
  const resolved = data?.restaurant;
  const name = c.meta?.name || resolved?.name;
  const image = c.meta?.image || resolved?.image;
  const slug = c.meta?.slug || resolved?.slug;
  const unavailable = resolved?.isActive === false;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.5,
        borderTop: first ? "none" : `1px solid ${LINE}`,
      }}
    >
      {image ? (
        <Box
          component="img"
          src={resizedImage(image, 128)}
          alt=""
          sx={{
            width: 44,
            height: 44,
            borderRadius: "10px",
            objectFit: "cover",
            flexShrink: 0,
            opacity: unavailable ? 0.45 : 1,
            filter: unavailable ? "grayscale(1)" : "none",
          }}
        />
      ) : (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "10px",
            backgroundColor: "#F7F8FA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <StorefrontIcon sx={{ fontSize: "1.25rem", color: MUTED }} />
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: INK,
            lineHeight: 1.4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name || "Saved cart"}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: MUTED, lineHeight: 1.4 }}>
          {c.count} {c.count === 1 ? "item" : "items"}
          {unavailable ? " · Not taking orders right now" : ""}
        </Typography>
      </Box>

      <Button
        disabled={unavailable}
        onClick={onCheckout}
        sx={{
          textTransform: "none",
          fontSize: "0.8rem",
          fontWeight: 700,
          borderRadius: "10px",
          px: 1.75,
          py: 0.6,
          color: ACCENT,
          border: `1.5px solid ${LINE}`,
          whiteSpace: "nowrap",
          "&:hover": { borderColor: ACCENT, backgroundColor: "#FFF3F0" },
        }}
      >
        Checkout
      </Button>

      <Button
        disabled={!slug || unavailable}
        onClick={() => onAddMore(slug)}
        sx={{
          textTransform: "none",
          fontSize: "0.8rem",
          fontWeight: 700,
          borderRadius: "10px",
          px: 1.75,
          py: 0.6,
          color: INK,
          border: `1.5px solid ${LINE}`,
          whiteSpace: "nowrap",
          "&:hover": { borderColor: "#C7CBD1", backgroundColor: "#FAFAFB" },
        }}
      >
        Add more items
      </Button>

      <IconButton
        size="small"
        aria-label="Remove cart"
        onClick={onDiscard}
        sx={{ color: "#C7CBD1", "&:hover": { color: ACCENT } }}
      >
        <DeleteOutlineIcon sx={{ fontSize: "1.2rem" }} />
      </IconButton>
    </Box>
  );
}

export default React.memo(CartsList);
